import { Button, ModuleHeader } from '@/components'
import { Input } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PlainLink } from '@/components_deprecated/Link'
import { LeaderGroupSelect } from '@/components_deprecated/Select/LeaderGroupSelect'
import { PermissionsRequired } from '@/containers/permission-containers'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import { useAdmin, useLeaderGroups, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import type { DateRange } from '@ur-solutions/react-components'
import { Icon } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useState } from 'react'
import { BrowserView, isMobileOnly, MobileView } from 'react-device-detect'
import styled from 'styled-components'
import { DateRangePickerCustom } from '../../../components_deprecated/DatePicker/DateRangePicker/DateRangePickerCustom'
import { IssuesTable } from '../components'
import { IssuePopupFilter } from '../components/IssuePopupFilter'
import type { IssuesTableFilter } from '../types'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  ${props => props.theme.layout.largePad} {
    padding: 122px 52px 52px;
    box-sizing: border-box;
  }
`
const Controls = styled(Card)`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1rem;
`
const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
`
const StyledInput = styled(Input)`
  ${isMobileOnly &&
  `
    width: 100%;
  `}
`

interface IssuesProps {}

export const Issues: React.FC<IssuesProps> = () => {
  const translations = useTranslate({
    issues: 'common.issues',
    createNew: 'common.create',
    findIssue: 'issues.find-issue',
    chooseGroup: 'common.choose-group',
    amount: 'common.amount',
    date: 'common.date',
  })

  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups(
    Module.ISSUES
  )
  const isAdmin = useAdmin()
  const { hasPermissions } = usePermissions()
  const canViewOthersIssue = hasPermissions(
    PERMISSIONS.issues.view.othersissue,
    true
  )

  const {
    formValues: filter,
    formChangeHandler: filterHandler,
    updateForm,
  } = useForm<IssuesTableFilter>({
    values: {
      query: '',
      orderBy: '-timeOfIssueStart',
      excludeCategories: [],
      open: undefined,
      archived: false,
      severities: [],
      onlyManuallyCreated: true,
      leaderGroup: null,
      leaderGroupUsers: [],
      date: [new Date(), new Date()],
      isDate: false,
      isDateStart: false,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        sessionStorage: true,
        storeFormStateName: 'firmadok__issue-overview-filter',
        transformValues: {},
      },
    },
  })

  const [dateRange, setDateRange] = useState<[Date, Date]>(
    filter?.date ?? [new Date(), new Date()]
  )
  const [localIsDate, setLocalIsDate] = useState<boolean>(true)
  const [localIsDateStart, setLocalIsDateStart] = useState<boolean>(
    filter.isDateStart ?? false
  )

  const handleButtonClick = async (date: {
    value: DateRange
    isDate: boolean
    isDateStart: boolean
  }) => {
    updateForm({
      ...filter,
      date: [date.value.from, date.value.to],
      isDate: date.isDate,
      isDateStart: date.isDateStart,
    })
  }

  const changeFilter = (value: string | null) => {
    const selectedGroup = myLeaderGroups.find(g => g.id === value)
    updateForm({
      leaderGroup: value,
      leaderGroupUsers: selectedGroup?.users.map(user => user.id) ?? [],
    })
  }

  const [filteredCount, setFilteredCount] = useState<number | undefined>(
    undefined
  )

  useEffect(() => {
    if (
      !canViewOthersIssue ||
      isAdmin ||
      filter.leaderGroup ||
      myLeaderGroups.length !== 1
    ) {
      return
    }

    const defaultLeaderGroup = myLeaderGroups[0]
    if (!defaultLeaderGroup) return

    updateForm({
      leaderGroup: defaultLeaderGroup.id,
      leaderGroupUsers: defaultLeaderGroup.users.map(user => user.id),
    })
  }, [
    canViewOthersIssue,
    filter.leaderGroup,
    isAdmin,
    myLeaderGroups,
    updateForm,
  ])

  return (
    <Wrapper>
      <ModuleHeader title={translations.issues} page={PageType.ISSUES}>
        <MobileView>
          <PermissionsRequired permissions={PERMISSIONS.issues.add.issue}>
            <Button asChild size="icon">
              <PlainLink to="/issues/create">
                <Icon icon="plus" />
              </PlainLink>
            </Button>
          </PermissionsRequired>
        </MobileView>

        <BrowserView>
          {typeof filteredCount !== 'undefined' && (
            <span className="text-primary">
              {translations.amount}: {filteredCount}
            </span>
          )}
        </BrowserView>
      </ModuleHeader>

      <Controls>
        <ButtonContainer>
          <StyledInput
            value={filter.query}
            type="search"
            placeholder={translations.findIssue}
            onChange={filterHandler('query')}
          />
          {filter.isDate !== undefined &&
            filter.isDateStart !== undefined &&
            filter?.date &&
            filter?.date?.length > 0 && (
              <DateRangePickerCustom
                value={{ from: dateRange[0], to: dateRange[1] }}
                onChange={date => setDateRange([date.from, date.to])}
                isDateStart={localIsDateStart}
                isDate={localIsDate}
                onToggleDate={(val, type) => {
                  if (type === 'isDate') {
                    setLocalIsDate(val)
                  } else {
                    setLocalIsDateStart(val)
                  }
                }}
                onButtonClick={handleButtonClick}
              />
            )}

          {hasAccess && canViewOthersIssue && (
            <LeaderGroupSelect
              value={filter.leaderGroup}
              loading={groupsLoading}
              options={
                myLeaderGroups?.map(group => ({
                  label: group.name,
                  value: group.id,
                })) ?? []
              }
              fullWidth
              grayed={true}
              onChange={value => changeFilter(value)}
              placeholder={translations.chooseGroup}
              nullable
            />
          )}
        </ButtonContainer>

        <BrowserView>
          <ButtonContainer>
            <IssuePopupFilter filter={filter} onUpdate={updateForm} />

            <PermissionsRequired permissions={PERMISSIONS.issues.add.issue}>
              <Button asChild>
                <PlainLink to="/issues/create">
                  <Icon icon="plus" />
                  {translations.createNew}
                </PlainLink>
              </Button>
            </PermissionsRequired>
          </ButtonContainer>
        </BrowserView>
      </Controls>

      <IssuesTable
        filter={filter}
        onSort={filterHandler('orderBy')}
        onFilterUpdate={({ count }) => {
          setFilteredCount(count)
        }}
      />
    </Wrapper>
  )
}
